import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, ChevronRight, SkipForward } from 'lucide-react';
import {
  ONBOARDING_STEP_DEFINITIONS,
  type OnboardingProgress,
  type OnboardingStepKey,
} from '@tailoring-crm/shared';
import { Button } from '@tailoring-crm/ui';
import { useSetOnboardingStep } from '../../lib/onboarding/use-onboarding.js';
import { SetupProgress } from './setup-progress.js';

/**
 * The setup checklist: six things, none of them compulsory.
 *
 * Each step links to the screen where the work actually happens rather than reproducing that
 * screen's form inside a wizard. Two reasons, and the second is the important one. A copy of
 * the GST section here would be a second form to keep in step with the first, and they would
 * drift. And a shop does not finish setting up once — an owner comes back to the tax section
 * in April and to the stage names when they hire a second karigar, so the place that work is
 * done has to be the place it lives, not a wizard they can only see on day one.
 *
 * Every step carries a Skip, and skipping is not hidden behind anything. A shop with no GST
 * registration must be able to say so in one tap and never be asked again.
 */

export interface SetupChecklistProps {
  progress: OnboardingProgress;
  /** Hides the progress bar where the surrounding screen already shows one. */
  showProgress?: boolean;
}

export function SetupChecklist({
  progress,
  showProgress = true,
}: SetupChecklistProps): ReactElement {
  const { t } = useTranslation();
  const setStep = useSetOnboardingStep();

  const byKey = new Map(progress.steps.map((step) => [step.key, step]));

  return (
    <div className="flex flex-col gap-4">
      {showProgress ? <SetupProgress progress={progress} /> : null}

      <ul className="flex flex-col gap-2">
        {ONBOARDING_STEP_DEFINITIONS.map((definition) => {
          const step = byKey.get(definition.key);
          // A step for a module this shop has not bought is not shown at all — it is not their
          // omission, and listing it would make the checklist a place to be sold to.
          if (!step?.available) return null;

          const done = step.status === 'completed';
          const skipped = step.status === 'skipped';

          return (
            <li
              key={definition.key}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3"
            >
              <span
                aria-hidden="true"
                className={
                  done
                    ? 'flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground'
                    : 'flex size-7 shrink-0 items-center justify-center rounded-full border border-dashed border-border text-muted-foreground'
                }
              >
                {done ? (
                  <Check className="size-4" />
                ) : skipped ? (
                  <SkipForward className="size-3.5" />
                ) : null}
              </span>

              <div className="min-w-0 flex-1">
                <p className="font-medium">{t(`onboarding.step.${definition.key}.title`)}</p>
                <p className="text-sm text-muted-foreground">
                  {done && step.autoCompleted
                    ? t('onboarding.alreadyDone')
                    : skipped
                      ? t('onboarding.skipped')
                      : t(`onboarding.step.${definition.key}.description`)}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <Button asChild variant={done ? 'ghost' : 'secondary'} size="sm">
                  <Link to={definition.path}>
                    {done ? t('onboarding.review') : t('onboarding.open')}
                    <ChevronRight aria-hidden="true" className="ml-1 size-4" />
                  </Link>
                </Button>
                {done ? null : (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={setStep.isPending}
                    onClick={() =>
                      setStep.mutate({
                        key: definition.key as OnboardingStepKey,
                        status: skipped ? 'pending' : 'skipped',
                      })
                    }
                  >
                    {skipped ? t('onboarding.unskip') : t('onboarding.skip')}
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
